import type {
  DividendApiResponse,
  DividendDatesApiData,
  DividendInfoApiData,
  DividendStockPriceHistoryCsv,
  DividendTransitionApiData,
  DividendTransitionApiResponse,
  DividendTransitionApiResult,
} from '@app-types/dividendApi';
import type { InstrumentQueryParams } from '@utils/queryParams';
import { dividendsClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

/** Dividend-flavoured alias of the shared envelope-error helper. */
const throwDividendApiError = (
  body: DividendApiResponse<unknown> | undefined,
  status: number | undefined,
  fallbackMessage: string,
): never => throwDataApiError(body, status, fallbackMessage);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDividendTransitionData = (
  value: unknown,
): value is DividendTransitionApiData =>
  Array.isArray(value) &&
  value.every(
    (parent) =>
      isRecord(parent) &&
      Array.isArray(parent.quarter) &&
      parent.quarter.every(isRecord),
  );

/**
 * Normalizes optional company-name metadata: a non-empty string is trimmed and
 * kept; missing, null, non-string or blank values collapse to `null` so the
 * blue stock series/legend can render `--` instead of hard-coded mock text.
 */
const normalizeCompanyName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

/**
 * GET /api/dividends/dividend-info?ticker=&mkt=
 * Sends the auth token via the `x-access-token` header (dividendsClient interceptor).
 */
export const fetchDividendInfo = async (
  params: InstrumentQueryParams,
): Promise<DividendInfoApiData> => {
  try {
    const response = await dividendsClient.get<DividendApiResponse<DividendInfoApiData>>(
      '/dividend-info',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    if (response.data.status !== 0 || !response.data.data) {
      throwDividendApiError(
        response.data,
        response.status,
        'Dividend info response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/dividends/dividend-dates?ticker=&mkt=
 * Returns the related dividend schedule dates.
 */
export const fetchDividendDates = async (
  params: InstrumentQueryParams,
): Promise<DividendDatesApiData> => {
  try {
    const response = await dividendsClient.get<DividendApiResponse<DividendDatesApiData>>(
      '/dividend-dates',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    if (response.data.status !== 0 || !response.data.data) {
      throwDividendApiError(
        response.data,
        response.status,
        'Dividend dates response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/dividends/dividend-transition?ticker=&mkt=&years=5|10
 * Returns grouped annual parents with nested quarterly/interim child rows.
 * `years` comes from the display-period dropdown and is always 5 or 10.
 */
export const fetchDividendTransition = async (
  params: InstrumentQueryParams & { years: number },
): Promise<DividendTransitionApiResult> => {
  try {
    const response = await dividendsClient.get<DividendTransitionApiResponse>(
      '/dividend-transition',
      {
        params: { ticker: params.ticker, mkt: params.mkt, years: params.years },
      },
    );

    const body = response.data;
    if (
      body.status !== 0 ||
      !isDividendTransitionData(body.data)
    ) {
      throwDividendApiError(
        body,
        response.status,
        'Dividend transition response is invalid.',
      );
    }

    // Company metadata is optional; missing/invalid names must not reject
    // otherwise valid grouped dividend rows.
    return {
      rows: body.data,
      nameEn: normalizeCompanyName(body.nameEn),
      nameJp: normalizeCompanyName(body.nameJp),
    };
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

export interface DividendStockPriceHistoryParams extends InstrumentQueryParams {
  /** Oldest visible annual chart date as YYYYMMDD. */
  from: string;
  /** Latest visible annual chart date as YYYYMMDD. */
  to: string;
  /** Optional Chart5 interval. Instructions requests daily data with `d`. */
  interval?: 'd';
}

/**
 * GET /api/dividends/dividend-transition/chart5-hts?ticker=&mkt=&from=&to=&i=
 * Returns the stock-price history for the blue dividend line as a raw CSV
 * string. `from`/`to` bound the selected visible annual range (from = oldest,
 * to = latest). Token is attached by the dividendsClient interceptor.
 */
export const fetchDividendStockPriceHistory = async (
  params: DividendStockPriceHistoryParams,
): Promise<DividendStockPriceHistoryCsv> => {
  try {
    const response = await dividendsClient.get<
      DividendApiResponse<DividendStockPriceHistoryCsv>
    >('/dividend-transition/chart5-hts', {
      params: {
        ticker: params.ticker,
        mkt: params.mkt,
        from: params.from,
        to: params.to,
        ...(params.interval ? { i: params.interval } : {}),
      },
    });

    const body = response.data;
    if (body.status !== 0 || typeof body.data !== 'string') {
      throwDividendApiError(
        body,
        response.status,
        'Dividend stock-price history response is invalid.',
      );
    }

    return body.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};
